import { NATIVE_PERMISSION_TYPE } from "core-lib/config/config";
import { FETCH_NATIVE_LOCATION } from "core-lib/constants/nativeBridge";
import {
  PermissionStatus,
  useCheckNativePermission,
} from "core-lib/hooks/useCheckNativePermission";
import { updateLocationRequest } from "core-lib/reduxStore/layout/action";
import { AddressStatusType } from "core-lib/types/Address";
import { UserLocation } from "core-lib/types/Location";
import { isLocationV2Available } from "core-lib/utils/locationV2";
import { passMessageToNative } from "core-lib/utils/nativeBridge";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Cookies, SessionStorage } from "../../constants/storage";
import { getCookieValue } from "../../utils/cookie";

export type AddressStateType = {
  coords: {
    lat: string;
    lng: string;
  };
  state?: string;
  status: AddressStatusType;
};

let callbackTimeout;
let statusCheckInterval;
export const useFetchNativeLocation = (
  hasMultipleLocationRequests?: any,
  locationV2RequestPayload: UserLocation = null
) => {
  const { status: permissionStatus, checkPermission } =
    useCheckNativePermission();

  const dispatch = useDispatch();

  const nativeEventCallRef = React.useRef(false);
  const intervalRef = React.useRef(false);
  const [loc, setLoc] = useState<AddressStateType>();

  const setLocation = () => {
    if (
      nativeEventCallRef.current &&
      (hasMultipleLocationRequests || !intervalRef.current)
    ) {
      intervalRef.current = true;
      if (loc && loc.status !== AddressStatusType.Pending)
        nativeEventCallRef.current = false;
      const status = sessionStorage.getItem(SessionStorage.nativeLocStatus);

      if (status === "SUCCESS") {
        const lat = sessionStorage.getItem(SessionStorage.nativeLocLatKey);

        const lng = sessionStorage.getItem(SessionStorage.nativeLocLngKey);

        const state = getCookieValue(Cookies.State);
        if (lat && lng) {
          setLoc({
            status: AddressStatusType.Success,
            state: state,
            coords: {
              lat,
              lng,
            },
          });
        } else {
          setLoc({
            status: AddressStatusType.Failed,
            state: null,
            coords: null,
          });
        }
      } else if (["FAILED", "DENY"].includes(status)) {
        setLoc({
          status: AddressStatusType.Denied,
          state: null,
          coords: null,
        });
      } else {
        console.warn("Did not receive valid status");
      }
      if (callbackTimeout) {
        clearTimeout(callbackTimeout);
      }
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    }
  };

  useEffect(() => {
    global.addEventListener("nativeStorage", setLocation);
    return () => {
      global.removeEventListener("nativeStorage", setLocation);
      clearInterval(statusCheckInterval);
      clearTimeout(callbackTimeout);
    };
  }, []);

  useEffect(() => {
    if (
      !locationV2RequestPayload &&
      !isLocationV2Available() &&
      [PermissionStatus.Success].includes(permissionStatus)
    ) {
      requestLocation();
    }
  }, [permissionStatus]);

  function requestLocation() {
    setLoc({
      status: AddressStatusType.Pending,
      coords: null,
    });
    nativeEventCallRef.current = true;
    const script = `
    try{
      
        const lat = "{{lat}}".replace(/'/g, "");
        const lng = "{{lng}}".replace(/'/g, "");
        const status = "{{status}}".replace(/'/g, "");
        window.sessionStorage.setItem('${SessionStorage.nativeLocStatus}', status);
        window.sessionStorage.setItem('${SessionStorage.nativeLocLatKey}', lat);
        window.sessionStorage.setItem('${SessionStorage.nativeLocLngKey}', lng);
        window.dispatchEvent(new Event('nativeStorage')); 
      } catch(e){
        console.error('callback_script error',e);
      }`;

    passMessageToNative(FETCH_NATIVE_LOCATION, null, {
      callback_script: script,
    });
  }

  function fetchLocation() {
    if (isLocationV2Available() && !!locationV2RequestPayload) {
      dispatch(updateLocationRequest(locationV2RequestPayload));
      return;
    }

    requestLocation();

    if (!hasMultipleLocationRequests && intervalRef.current) return;

    callbackTimeout = setTimeout(() => {
      if (nativeEventCallRef.current) {
        nativeEventCallRef.current = false;
        console.warn("Did not receive callback in 30 second time");
        setLoc({
          status: AddressStatusType.Failed,
          coords: null,
        });
      }
      clearTimeout(callbackTimeout);
      clearInterval(statusCheckInterval);
    }, 30000);

    if (statusCheckInterval) clearInterval(statusCheckInterval);
    statusCheckInterval = setInterval(() => {
      checkPermission(NATIVE_PERMISSION_TYPE.Location);
    }, 2000);
  }

  return { fetchLocation, loc };
};
